import React, { useState } from 'react';
import { Plus, ArrowRight, Trash2, Search } from 'lucide-react';
import {
  Button, Input, Textarea, Select, ChipSelect, RankList, Pill, StagePill,
  Badge, Avatar, Card, Modal, FloatingPanel, Toast, Tooltip, RankItem,
} from '../components/ui';
import Section from './design/Section';
import RobbieGallery from './design/RobbieGallery';
import TableGallery from './design/TableGallery';
import ShellGallery from './design/ShellGallery';
import CreditsGallery from './design/CreditsGallery';
import WizardChromeGallery from './design/WizardChromeGallery';
import UploadStepGallery from './design/UploadStepGallery';
import CriteriaGallery from './design/CriteriaGallery';
import ContextStepGallery from './design/ContextStepGallery';
import ValidationStepGallery from './design/ValidationStepGallery';
import RobbiePanelGallery from './design/RobbiePanelGallery';
import DashboardGallery from './design/DashboardGallery';
import FirstRunGallery from './design/FirstRunGallery';
import WelcomeGallery from './design/WelcomeGallery';
import DashboardListGallery from './design/DashboardListGallery';
import DiscoveryGallery from './design/DiscoveryGallery';
import OutreachGallery from './design/OutreachGallery';
import QuotesGallery from './design/QuotesGallery';
import ShareGallery from './design/ShareGallery';
import SmartBomGallery from './design/SmartBomGallery';
import CriteriaBriefGallery from './design/CriteriaBriefGallery';
import NotificationsGallery from './design/NotificationsGallery';
import LoadingGallery from './design/LoadingGallery';

/**
 * /design — dev page to eyeball every v2 primitive state side by side with
 * `design/Design System.dc.html` sections 05–06. Not linked from app nav.
 * `/design?only=<key>` renders a single gallery (deep-linkable, and keeps the
 * page short enough for full-page screenshots in review tooling).
 */

const GALLERY_KEYS = [
  'robbie', 'table', 'shell', 'credits', 'chrome', 'upload', 'criteria',
  'context', 'validation', 'robbie-panel', 'dashboard', 'first-run',
  'welcome', 'dashboard-list', 'discovery', 'outreach', 'quotes', 'share', 'notifications',
  'smartbom', 'criteria-brief', 'loading', 'primitives',
] as const;
type GalleryKey = (typeof GALLERY_KEYS)[number];

const DesignSystem: React.FC = () => {
  const only = new URLSearchParams(window.location.search).get('only') as GalleryKey | null;
  const show = (key: GalleryKey) => !only || only === key;
  const [chips, setChips] = useState<string[]>(['iso9001']);
  const [leadTime, setLeadTime] = useState<string[]>(['8w']);
  const [rank, setRank] = useState<RankItem[]>([
    { id: 'cost', label: 'Cost', description: 'landed unit price' },
    { id: 'lead', label: 'Lead time', description: 'ex-works weeks' },
    { id: 'trust', label: 'Supplier trust', description: 'track record' },
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [toastVisible, setToastVisible] = useState(true);

  return (
    <main className="min-h-screen bg-canvas px-s7 py-s7 font-sans">
      {/* Wider than the old 860 canvas — the center panel went fluid, so
          galleries preview components at a realistic width. */}
      <div className="mx-auto flex max-w-[1240px] flex-col gap-s7">
        <header>
          <h1 className="text-page-title text-ink">Design system — v2 primitives</h1>
          <p className="mt-s1 text-body-md text-ink-secondary">
            Compare with <span className="font-mono text-mono-data">design/Design System.dc.html</span> section 06.
            Navy chrome · cobalt actions · violet Robbie-only · azure signal.
          </p>
        </header>

        {show('robbie') && <RobbieGallery />}
        {show('table') && <TableGallery />}
        {show('shell') && <ShellGallery />}
        {show('credits') && <CreditsGallery />}
        {show('chrome') && <WizardChromeGallery />}
        {show('upload') && <UploadStepGallery />}
        {show('criteria') && <CriteriaGallery />}
        {show('context') && <ContextStepGallery />}
        {show('validation') && <ValidationStepGallery />}
        {show('robbie-panel') && <RobbiePanelGallery />}
        {show('dashboard') && <DashboardGallery />}
        {show('first-run') && <FirstRunGallery />}
        {show('welcome') && <WelcomeGallery />}
        {show('dashboard-list') && <DashboardListGallery />}
        {show('discovery') && <DiscoveryGallery />}
        {show('outreach') && <OutreachGallery />}
        {show('quotes') && <QuotesGallery />}
        {show('share') && <ShareGallery />}
        {show('notifications') && <NotificationsGallery />}
        {show('smartbom') && <SmartBomGallery />}
        {show('criteria-brief') && <CriteriaBriefGallery />}
        {show('loading') && <LoadingGallery />}

        {show('primitives') && (<>
        <Section title="Buttons">
          <div className="flex flex-col gap-s4">
            <div className="flex flex-wrap items-center gap-s3">
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button disabled>Disabled</Button>
            </div>
            <div className="flex flex-wrap items-center gap-s3">
              <Button size="sm" iconLeft={<Plus />}>Small</Button>
              <Button size="md" iconLeft={<Plus />}>Medium</Button>
              <Button size="lg" iconRight={<ArrowRight />}>Large</Button>
              <Button variant="secondary" iconLeft={<Search />}>With icon</Button>
              <Button variant="danger" size="sm" iconLeft={<Trash2 />}>Delete</Button>
            </div>
          </div>
        </Section>

        <Section title="Fields">
          <div className="grid grid-cols-1 gap-s6 md:grid-cols-2">
            <Input label="Project name" placeholder="e.g. AGV drivetrain refresh" helper="Shown to suppliers on the RFQ." />
            <Input label="Target unit cost" placeholder="0.00" error="Enter a number greater than zero." required />
            <Select
              label="Payment terms"
              placeholder="Choose terms"
              options={[
                { value: 'net30', label: 'Net 30' },
                { value: 'net60', label: 'Net 60' },
                { value: 'milestone', label: 'Milestone-based' },
              ]}
              helper="Standard is Net 30."
            />
            <Textarea label="Notes for suppliers" placeholder="Anything they should know…" />
          </div>
        </Section>

        <Section title="Chip select">
          <div className="flex flex-col gap-s6">
            <ChipSelect
              label="Compliance"
              options={[
                { value: 'iso9001', label: 'ISO 9001' },
                { value: 'rohs', label: 'RoHS' },
                { value: 'reach', label: 'REACH' },
                { value: 'ul', label: 'UL' },
              ]}
              value={chips}
              onChange={setChips}
            />
            <ChipSelect
              label="Lead time"
              mode="single"
              options={[
                { value: '4w', label: '4 weeks' },
                { value: '8w', label: '8 weeks' },
                { value: '12w', label: '12 weeks' },
              ]}
              value={leadTime}
              onChange={setLeadTime}
            />
          </div>
        </Section>

        <Section title="Drag to rank">
          <div className="max-w-md overflow-hidden rounded-card border border-line bg-surface">
            <RankList items={rank} onReorder={setRank} />
          </div>
        </Section>

        <Section title="Pills, stages, badges, avatars">
          <div className="flex flex-col gap-s4">
            <div className="flex flex-wrap items-center gap-s3">
              <Pill status="success" dot>Quote received</Pill>
              <Pill status="warn" dot>Awaiting reply</Pill>
              <Pill status="danger" dot>Missed deadline</Pill>
              <Pill status="neutral">Draft</Pill>
            </div>
            <div className="flex flex-wrap items-center gap-s3">
              <StagePill stage={1}>1 · Upload BOM</StagePill>
              <StagePill stage={2}>2 · Criteria</StagePill>
              <StagePill stage={3}>3 · Discovery</StagePill>
              <StagePill stage={4}>4 · Outreach</StagePill>
              <StagePill stage={5}>5 · Quotes</StagePill>
            </div>
            <div className="flex flex-wrap items-center gap-s3">
              <Badge>12</Badge>
              <Badge tone="action" mono>RFQ-0042</Badge>
              <Badge tone="robbie">Robbie insight chip</Badge>
              <Badge tone="robbie" mark={false}>2 insights</Badge>
              <Avatar name="Keerthi Vasan" size="sm" />
              <Avatar name="Keerthi Vasan" />
              <Avatar name="Priya N" size="lg" />
            </div>
          </div>
        </Section>

        <Section title="Cards">
          <div className="grid grid-cols-1 gap-s6 md:grid-cols-2">
            <Card title="Supplier shortlist" actions={<Button size="sm" variant="secondary">Edit</Button>}
              footer={<span className="text-meta text-ink-muted">Updated 2h ago</span>}>
              <p className="text-body-md text-ink-body">
                Plain card: 1px border, radius 10, no shadow. Header at 13.5/700.
              </p>
            </Card>
            <Card ai title="Robbie’s read on your quotes" actions={<Badge tone="robbie">Robbie</Badge>}>
              <p className="text-body-md text-ink-body">
                AI-carrying card: subtle blue→violet wash. I’d split this award —
                Vendor A misses your Q4 date on 2 parts. Ask Robbie why.
              </p>
            </Card>
          </div>
        </Section>

        <Section title="Modal + floating panel">
          <div className="flex flex-wrap items-start gap-s6">
            <Button onClick={() => setModalOpen(true)}>Open modal</Button>
            <FloatingPanel className="w-64 p-s5">
              <p className="text-row-title text-ink">Floating panel</p>
              <p className="mt-s1 text-body-sm text-ink-secondary">Radius 14 + the one permitted shadow.</p>
            </FloatingPanel>
            <FloatingPanel ai className="w-64 p-s5">
              <p className="text-row-title text-robbie-ink">Robbie panel</p>
              <p className="mt-s1 text-body-sm text-ink-secondary">AI wash — violet stays Robbie’s.</p>
            </FloatingPanel>
          </div>
          <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Remove supplier?"
            footer={
              <>
                <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button variant="danger" onClick={() => setModalOpen(false)}>Remove</Button>
              </>
            }
          >
            <p className="text-body-md text-ink-body">
              Removing Vendor B drops their quote from the comparison. You can re-invite them later.
            </p>
          </Modal>
        </Section>

        <Section title="Toast + tooltip">
          <div className="flex flex-col items-start gap-s4">
            {toastVisible && (
              <Toast status="success" title="Outreach sent" message="6 suppliers received your RFQ."
                onDismiss={() => setToastVisible(false)} />
            )}
            <Toast status="warn" title="2 suppliers haven’t replied" />
            <Toast status="danger" title="Send failed" message="Vendor C’s contact bounced." />
            <Tooltip content="Prices shown are landed cost, incl. freight.">
              <Button variant="secondary" size="sm">Hover me</Button>
            </Tooltip>
          </div>
        </Section>
        </>)}
      </div>
    </main>
  );
};

export default DesignSystem;
